import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';

import { LOAD_STORED_STATE, type LoadStoredStateAction } from './constants';

export interface TableConfigType {
  size: 'large' | 'middle' | 'small';
}

export interface ManageLeadsState {
  isAdvancedSearchOpen: boolean;
  isEmailOpen: boolean;
  sidebarVisible: boolean;
  page: number;
  loadedTableData?: {
    totalCount?: number;
  },
  leadList: {
    itemsPerPage: number;
    visibleColumnsV1?: string[];
    tableConfig: TableConfigType;
  },
}

const initialState: ManageLeadsState = {
  isAdvancedSearchOpen: false,
  isEmailOpen: false,
  sidebarVisible: false,
  page: 1,
  leadList: {
    itemsPerPage: 10,
    visibleColumnsV1: [],
    tableConfig: {
      size: 'large',
    },
  },
};

export const manageLeadsSlice = createSlice({
  name: 'manageLeads',
  initialState,
  reducers: {
    toggleAdvancedSearch: (state) => {
      state.isAdvancedSearchOpen = !state.isAdvancedSearchOpen;
    },
    toggleEmail: (state) => {
      state.isEmailOpen = !state.isEmailOpen;
    },
    setSidebarOpen: (state, action: PayloadAction<boolean>) => {
      state.sidebarVisible = action.payload;
    },
    updateItemsPerPage: (state, action: PayloadAction<number>) => {
      state.leadList.itemsPerPage = action.payload;
    },
    updateCurrentPage: (state, action: PayloadAction<number>) => {
      state.page = action.payload;
    },
    resetCurrentPage: (state) => {
      state.page = initialState.page;
    },
    updateTableConfig: (state, action: PayloadAction<Partial<TableConfigType>>) => {
      state.leadList.tableConfig = {
        ...state.leadList.tableConfig,
        ...action.payload,
      };
    },
    setVisibleColumns: (state, action: PayloadAction<string[]>) => {
      state.leadList.visibleColumnsV1 = action.payload;
    },
    setLoadedTableData: (state, action: PayloadAction<{ totalCount?: number }>) => {
      state.loadedTableData = action.payload;
    },
    resetManageLeadsPage: () => initialState,
  },
  extraReducers: (builder) => {
    builder.addCase(LOAD_STORED_STATE, (_, action: LoadStoredStateAction) => ({
      ...initialState,
      ...action.payload.manageLeads,
    }));
  },
});

export const {
  toggleAdvancedSearch,
  toggleEmail,
  setSidebarOpen,
  updateItemsPerPage,
  updateCurrentPage,
  resetCurrentPage,
  updateTableConfig,
  setVisibleColumns,
  resetManageLeadsPage,
  setLoadedTableData,
} = manageLeadsSlice.actions;

export default manageLeadsSlice.reducer;
